import {
  Alert,
  Button,
  DatePicker,
  Drawer,
  Form,
  Input,
  Select,
  Space,
} from 'antd'
import React, { useCallback, useEffect, useState } from 'react'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import localeData from 'dayjs/plugin/localeData'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import weekday from 'dayjs/plugin/weekday'

// ---------- Dayjs Plugins ----------
dayjs.extend(isBetween)
dayjs.extend(weekday)
dayjs.extend(localeData)
dayjs.extend(customParseFormat)

const { TextArea } = Input

const DATE_FORMAT = 'DD-MM-YYYY'

// ---------- Helpers ----------

const isOverlap = (aStart, aEnd, bStart, bEnd) => {
  // Check new date range falls between existing record
  if (aStart.isBetween(bStart, bEnd)) {
    return true
  } else if (aEnd.isBetween(bStart, bEnd)) {
    return true
  }

  // Check existing record falls between new date range
  if (bStart.isBetween(aStart, aEnd)) {
    return true
  } else if (bEnd.isBetween(aStart, aEnd)) {
    return true
  }

  return false
}

const URL_PATTERN = /^(https?:\/\/)[^\s/$.?#].[^\s]*$/i

// ---------- Component ----------
const BannerDrawer = ({
  banner,
  onClose,
  onSubmit,
  submitting,
  visible,
  banners,
}) => {
  const { t } = useTranslation()

  const [form] = Form.useForm()

  const [overlapError, setOverlapError] = useState(null)

  const isEdit = !!banner

  // ---------- Disable Dates ----------
  const disablePastDates = current =>
    current && current < dayjs().startOf('day')

  const disableEndDates = current => {
    const startDate = form.getFieldValue('startDate')

    return startDate && current && current < startDate.startOf('day')
  }

  // ---------- Overlap Check ----------
  const checkOverlap = useCallback(
    (startDate, endDate) => {
      // if dates are invalid, don't run overlap validation
      if (!startDate || !endDate || endDate.isBefore(startDate, 'day')) {
        setOverlapError(null)

        return
      }

      const sortedBanner = [...(banners ?? [])].sort((a, b) =>
        dayjs(a.fromDate).isBefore(dayjs(b.fromDate)) ? -1 : 1
      )

      const conflict = sortedBanner.find(b => {
        if (!b.fromDate || !b.toDate) return false
        if (isEdit && b.id === banner?.id) return false

        return isOverlap(
          dayjs(startDate),
          dayjs(endDate),
          dayjs(b.fromDate),
          dayjs(b.toDate)
        )
      })

      setOverlapError(conflict || null)
    },
    [banners, banner, isEdit]
  )

  // ---------- Effects ----------
  useEffect(() => {
    if (!visible) return

    if (!banner) {
      form.resetFields()
      setOverlapError(null)

      return
    }

    const values = {
      ...banner,

      startDate: banner.fromDate ? dayjs(banner.fromDate) : null,

      endDate: banner.toDate ? dayjs(banner.toDate) : null,

      link: banner.link || '',
    }

    form.setFieldsValue(values)

    checkOverlap(values.startDate, values.endDate)
  }, [visible, banner, form, checkOverlap])

  // ---------- Submit ----------
  const handleFinish = values => {
    const payload = {
      category: values.category,
      content: values.content || '',
      link: values.link || null,
      title: values.title,

      fromDate: values.startDate
        ? values.startDate
            .startOf('day')
            .add(1, 'second')
            .format('YYYY-MM-DDTHH:mm:ss.SSS')
        : null,

      toDate: values.endDate
        ? values.endDate.endOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS')
        : null,
    }

    onSubmit(
      isEdit
        ? {
            id: banner.id,
            ...payload,
          }
        : payload
    )
  }

  // ---------- Derived ----------
  const categoryOptions = ['G', 'M', 'R'].map(value => ({
    label: t(`admin.bannerManagement.categories.${value}`),
    value,
  }))

  const text = {
    submit: t(
      isEdit
        ? 'admin.bannerManagement.actions.updateBanner'
        : 'admin.bannerManagement.actions.createBanner'
    ),

    title: t(
      isEdit
        ? 'admin.bannerManagement.drawerTitleEdit'
        : 'admin.bannerManagement.drawerTitleCreate'
    ),
  }

  // ---------- Render ----------
  return (
    <Drawer
      destroyOnClose
      onClose={onClose}
      open={visible}
      title={<div style={{ fontWeight: 600 }}>{text.title}</div>}
      width={420}
      footer={
        <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
          <Button disabled={submitting} onClick={onClose}>
            {t('admin.bannerManagement.actions.cancel')}
          </Button>

          <Button
            disabled={!!overlapError}
            loading={submitting}
            onClick={form.submit}
            type="primary"
          >
            {text.submit}
          </Button>
        </Space>
      }
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        requiredMark={false}
        onValuesChange={() =>
          checkOverlap(
            form.getFieldValue('startDate'),
            form.getFieldValue('endDate')
          )
        }
      >
        {/* Category */}
        <Form.Item
          label={t('admin.bannerManagement.fields.category')}
          name="category"
          rules={[
            {
              message: t('admin.bannerManagement.validation.categoryRequired'),
              required: true,
            },
          ]}
        >
          <Select
            options={categoryOptions}
            placeholder={t('admin.bannerManagement.fields.category')}
          />
        </Form.Item>

        {/* Headline */}
        <Form.Item
          label={t('admin.bannerManagement.fields.headline')}
          name="title"
          rules={[
            {
              message: t('admin.bannerManagement.validation.titleMinChars'),
              min: 10,
            },
            {
              message: t('admin.bannerManagement.validation.titleRequired'),
              required: true,
              whitespace: true,
            },
          ]}
        >
          <TextArea
            autoSize={{ maxRows: 4, minRows: 2 }}
            maxLength={150}
            showCount
            style={{ fontSize: 16 }}
          />
        </Form.Item>

        {/* Start Date */}
        <Form.Item
          label={t('admin.bannerManagement.fields.startDate')}
          name="startDate"
          rules={[
            {
              message: t('admin.bannerManagement.validation.startDateRequired'),
              required: true,
            },
          ]}
        >
          <DatePicker
            disabledDate={disablePastDates}
            format={DATE_FORMAT}
            style={{
              width: '100%',
            }}
          />
        </Form.Item>

        {/* End Date */}
        <Form.Item
          dependencies={['startDate']}
          label={t('admin.bannerManagement.fields.endDate')}
          name="endDate"
          rules={[
            {
              message: t('admin.bannerManagement.validation.endDateRequired'),
              required: true,
            },

            // This always checks endDate >= startDate
            ({ getFieldValue }) => ({
              validator(_, value) {
                const startDate = getFieldValue('startDate')

                if (!value || !startDate) {
                  return Promise.resolve()
                }

                if (value.isBefore(startDate, 'day')) {
                  return Promise.reject(
                    new Error(
                      t('admin.bannerManagement.validation.endDateAfterStart')
                    )
                  )
                }

                return Promise.resolve()
              },
            }),
          ]}
        >
          <DatePicker
            disabledDate={disableEndDates}
            format={DATE_FORMAT}
            style={{
              width: '100%',
            }}
          />
        </Form.Item>

        {/* Overlap Alert */}
        {overlapError && (
          <Alert
            message={t('admin.bannerManagement.overlap.title')}
            showIcon
            type="error"
            description={t('admin.bannerManagement.overlap.description', {
              title: overlapError.title,

              startDate: overlapError.fromDate
                ? dayjs(overlapError.fromDate).format(DATE_FORMAT)
                : '—',

              endDate: overlapError.toDate
                ? dayjs(overlapError.toDate).format(DATE_FORMAT)
                : '—',
            })}
            style={{
              marginBottom: 12,
            }}
          />
        )}

        {/* Link */}
        <Form.Item
          extra={t('admin.bannerManagement.hints.link')}
          label={t('admin.bannerManagement.fields.linkOptional')}
          name="link"
          rules={[
            {
              validator: (_, value) =>
                !value || URL_PATTERN.test(value)
                  ? Promise.resolve()
                  : Promise.reject(
                      new Error(
                        t('admin.bannerManagement.validation.linkInvalid')
                      )
                    ),
            },
          ]}
        >
          <Input />
        </Form.Item>
      </Form>
    </Drawer>
  )
}

// ---------- Props ----------
BannerDrawer.defaultProps = {
  banner: null,
  banners: [],
}

BannerDrawer.propTypes = {
  banner: PropTypes.object,
  banners: PropTypes.array,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  submitting: PropTypes.bool.isRequired,
  visible: PropTypes.bool.isRequired,
}

export default BannerDrawer
